import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { SITE_URL } from "@/lib/useDocumentMeta";

export const fade = {
    hidden: { opacity: 0, y: 16 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.55, ease: [0.16, 1, 0.3, 1] } }),
};

export const breadcrumbLd = (trail) => ({
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.label, item: `${SITE_URL}${t.to}` })),
});

export const faqLd = (faq, url) => ({
    "@type": "FAQPage", "@id": `${SITE_URL}${url}#faq`,
    mainEntity: faq.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const Breadcrumbs = ({ trail }) => (
    <nav aria-label="Breadcrumb" className="text-xs font-mono text-ink2 flex flex-wrap items-center gap-1" data-testid="breadcrumb-nav">
        {trail.map((t, i) => (
            <span key={t.to} className="inline-flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-3 h-3" />}
                {i < trail.length - 1 ? <Link to={t.to} className="hover:text-klein">{t.label}</Link> : <span className="text-ink">{t.label}</span>}
            </span>
        ))}
    </nav>
);

export const ShortAnswer = ({ children, label = "Short answer" }) => (
    <aside className="border-l-[3px] border-klein bg-paperWarm px-5 py-4 my-8" data-testid="answer-first-summary">
        <div className="overline text-klein mb-2">{label}</div>
        <p className="leading-relaxed text-ink">{children}</p>
    </aside>
);

export const CompareTable = ({ table }) => (
    <div className="overflow-x-auto my-8 border border-line bg-white" data-testid="comparison-matrix-table">
        <table className="w-full text-sm min-w-[520px]">
            <thead><tr className="bg-ink text-paper">{table.head.map(h => <th key={h} scope="col" className="text-left font-mono text-xs uppercase tracking-wider px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{table.rows.map((r, i) => (
                <tr key={i} className="border-t border-line align-top hover:bg-paperWarm transition-colors">
                    {r.map((c, j) => j === 0 ? <th key={j} scope="row" className="text-left font-semibold px-4 py-3">{c}</th> : <td key={j} className="px-4 py-3 text-ink2">{c}</td>)}
                </tr>
            ))}</tbody>
        </table>
    </div>
);

export const CodeBlock = ({ code, label }) => (
    <figure className="my-8 bg-[#141414] text-[#EDEDED] border border-ink" data-testid="schema-code-viewer">
        {label && <figcaption className="px-4 py-2 border-b border-white/10 text-[11px] font-mono uppercase tracking-wider text-white/60">{label}</figcaption>}
        <pre className="p-4 overflow-x-auto text-xs leading-relaxed font-mono"><code>{code}</code></pre>
    </figure>
);

export const FaqList = ({ faq }) => (
    <dl className="divide-y divide-line border-y border-line" data-testid="page-faq">
        {faq.map((f, i) => (
            <div key={f.q} className="py-5" data-testid={`page-faq-${i + 1}`}>
                <dt className="font-semibold text-base sm:text-lg">{f.q}</dt>
                <dd className="mt-2 text-ink2 leading-relaxed">{f.a}</dd>
            </div>
        ))}
    </dl>
);

export const CtaBand = ({ title = "Publish your first recipe article today.", text = "Free to use. Bring your own Anthropic and kie.ai keys, connect WordPress with an Application Password, and generate in minutes." }) => (
    <section className="bg-klein text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-[1fr_auto] gap-8 items-end">
            <div>
                <h2 className="heading text-3xl sm:text-4xl leading-tight">{title}</h2>
                <p className="text-white/75 mt-3 max-w-xl leading-relaxed text-sm sm:text-base">{text}</p>
            </div>
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-klein hover:bg-paperWarm font-medium transition-colors" data-testid="cta-band-register">
                Create free account <ArrowRight className="w-4 h-4" />
            </Link>
        </div>
    </section>
);

export const Reveal = ({ children, i = 0, className = "" }) => (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fade} custom={i} className={className}>{children}</motion.div>
);
